import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { File as FileResource } from '@terafile/shared'
import { useEffect, useRef, useState } from 'react'
import { FETCH_ERROR_FALLBACK, SUBMIT_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'
import { type FileKind, classifyFile, iconForKind, isPreviewable } from '../lib/file-kind.js'
import {
  archiveFile,
  getDownloadUrl,
  listFiles,
  renameFile,
  unarchiveFile,
} from '../lib/files-api.js'
import {
  LOADING,
  TRASH_ACTION,
  TRASH_RETENTION_DAYS,
  getTrashRemainingDays,
} from '../lib/labels.js'
import { useToast } from '../lib/toast.js'
import { useInView } from '../lib/use-in-view.js'
import { usePreviewUrl, useStreamThumbnailUrl } from '../lib/use-preview-url.js'
import { CreateShareLinkDialog } from './CreateShareLinkDialog.js'
import { DeleteFileModal } from './DeleteFileModal.js'
import { FileViewerModal } from './FileViewerModal.js'
import { MoveToTrashModal, type TrashTarget } from './MoveToTrashModal.js'
import { VideoPlayerModal } from './VideoPlayerModal.js'

type Props = {
  projectId: string
}

/**
 * プロジェクト配下のファイル一覧。
 *
 * - 動画: クリックで Cloudflare Stream プレイヤーが上部に展開
 * - 画像 / PDF: クリックでフルプレビュー モーダル
 * - 音声・その他: DL のみ（プレビュー不可）
 * - 各行に サムネ / 状態バッジ / DL / 削除 操作
 */
export function FileList({ projectId }: Props) {
  // ゴミ箱タブを表示するか（デフォルトは active のみ）
  const [showTrash, setShowTrash] = useState(false)
  const queryClient = useQueryClient()
  const toast = useToast()

  const { data, isLoading, error } = useQuery({
    queryKey: ['files', projectId, showTrash],
    queryFn: () => listFiles(projectId, { archived: showTrash }),
    // 動画変換待機中（PENDING/QUEUED）のファイルがある時だけ 10s polling で stream_status を取り直す。
    // - 非動画ファイル (mimeType が video/ で始まらない) は対象外
    //   → 旧実装はここで止まらず非動画ファイルでも永続 polling していた
    // - 5s だと体感うざいので 10s に緩和
    refetchInterval: (query) =>
      query.state.data?.items.some(
        (f) =>
          f.mimeType.startsWith('video/') &&
          (f.streamStatus === 'PENDING' || f.streamStatus === 'QUEUED'),
      )
        ? 10000
        : false,
  })
  // 動画再生中の選択（VideoPlayer 用）
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null)
  // 画像 / PDF プレビュー対象
  const [previewTarget, setPreviewTarget] = useState<FileResource | null>(null)
  // ゴミ箱へ移す確認対象
  const [trashTarget, setTrashTarget] = useState<TrashTarget | null>(null)
  // 今すぐ完全に消す対象（ゴミ箱タブのみ）
  const [deleteTarget, setDeleteTarget] = useState<FileResource | null>(null)
  // 一括選択（チェックボックス）。「選択 → 共有」のフロー用。
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<string>>(new Set())
  // 現在ファイル名を編集中の fileId。同時に複数行を編集モードにさせないための排他制御。
  const [editingFileId, setEditingFileId] = useState<string | null>(null)
  // 共有URL作成ダイアログを開いているか + その時のプリセット fileIds
  const [shareDialogOpen, setShareDialogOpen] = useState(false)
  const [shareInitialFileIds, setShareInitialFileIds] = useState<string[]>([])

  // タブ切替で選択状態をリセット（古い ID が残らないように）
  // biome-ignore lint/correctness/useExhaustiveDependencies: showTrash の変化を「トリガー」として使う意図的な書き方
  useEffect(() => {
    setBulkSelectedIds(new Set())
  }, [showTrash])

  const toggleBulkSelected = (fileId: string) => {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(fileId)) next.delete(fileId)
      else next.add(fileId)
      return next
    })
  }

  const openShareWithSelection = (fileIds: string[]) => {
    setShareInitialFileIds(fileIds)
    setShareDialogOpen(true)
  }

  const archiveMut = useMutation({
    mutationFn: archiveFile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files', projectId] })
      toast.success(TRASH_ACTION.toastMovedFile)
      setTrashTarget(null)
    },
    onError: (err) => {
      toast.error(toUserMessage(err, SUBMIT_ERROR_FALLBACK))
    },
  })
  const unarchiveMut = useMutation({
    mutationFn: unarchiveFile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files', projectId] })
      toast.success(TRASH_ACTION.toastRestoredFile)
    },
    onError: (err) => {
      toast.error(toUserMessage(err, SUBMIT_ERROR_FALLBACK))
    },
  })

  // 一覧が更新されて選択ファイルが消えていたら選択を解除
  useEffect(() => {
    if (selectedVideoId && data && !data.items.find((f) => f.id === selectedVideoId)) {
      setSelectedVideoId(null)
    }
  }, [data, selectedVideoId])

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">{LOADING.fetching}</p>
  }
  if (error) {
    const msg = toUserMessage(error, FETCH_ERROR_FALLBACK)
    return (
      <div className="text-sm text-destructive bg-destructive/5 border border-destructive/30 rounded-md p-3">
        {msg}
      </div>
    )
  }
  const items = data?.items ?? []
  // `Array.find` の戻り値は T | undefined。VideoPlayerModal は null を期待するので明示的に正規化。
  const selectedVideo = (selectedVideoId && items.find((f) => f.id === selectedVideoId)) || null
  const tabBusy = archiveMut.isPending || unarchiveMut.isPending

  // 共有可能なファイル（アップロード完了済み、ゴミ箱に入っていない）
  const shareableItems = items.filter((f) => !!f.uploadedAt && !f.archivedAt)
  const inSelectionMode = bulkSelectedIds.size > 0 && !showTrash

  return (
    <div className="space-y-3">
      {/* 選択モードのアクションバー（Google Drive 風）。
          チェックを 1 件以上入れた時だけ出現し、操作対象を絞り込む。
          普段のタブ UI と共存させるとボタンだらけで分かりにくいので、
          選択中はバーで覆ってしまう (issue #32)。*/}
      {inSelectionMode ? (
        <SelectionActionBar
          selectedCount={bulkSelectedIds.size}
          onClear={() => setBulkSelectedIds(new Set())}
          onShare={() => openShareWithSelection(Array.from(bulkSelectedIds))}
        />
      ) : (
        <div className="flex items-center gap-3 border-b border-border">
          <TabButton
            active={!showTrash}
            onClick={() => setShowTrash(false)}
            label={TRASH_ACTION.activeTab}
          />
          <TabButton
            active={showTrash}
            onClick={() => setShowTrash(true)}
            label={TRASH_ACTION.tab}
          />
        </div>
      )}

      {/* 動画再生はモーダル展開する（旧: 一覧上部のインライン展開だと
          ファイル数が多い時に下のファイルを再生したらスクロール戻しが発生し、
          そもそも再生が始まったことに気づきにくかった）*/}
      <VideoPlayerModal file={selectedVideo} onClose={() => setSelectedVideoId(null)} />

      {items.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg py-10 text-center bg-muted/30">
          <p className="text-sm text-muted-foreground">
            {showTrash ? 'ゴミ箱に入っているファイルはありません' : 'まだファイルはありません'}
          </p>
          {!showTrash && (
            <p className="text-xs text-muted-foreground/70 mt-1">
              上のエリアにドラッグ & ドロップ、または「ファイルを選択」から開始
            </p>
          )}
        </div>
      ) : (
        <>
          {/* 一括選択用のヘッダ行（"全て選択" + 件数表示）。ゴミ箱タブでは出さない（共有しないので） */}
          {!showTrash && (
            <BulkSelectionHeader
              total={items.length}
              selected={bulkSelectedIds.size}
              onSelectAll={() => setBulkSelectedIds(new Set(shareableItems.map((f) => f.id)))}
              onClear={() => setBulkSelectedIds(new Set())}
            />
          )}
          <ul className="space-y-2">
            {items.map((file) => (
              <FileRow
                key={file.id}
                file={file}
                isSelected={file.id === selectedVideoId}
                bulkChecked={bulkSelectedIds.has(file.id)}
                bulkSelectable={!showTrash && !!file.uploadedAt}
                isInSelectionMode={inSelectionMode}
                onToggleBulkChecked={() => toggleBulkSelected(file.id)}
                busy={tabBusy}
                onPlayVideo={() => setSelectedVideoId(file.id)}
                onPreview={() => setPreviewTarget(file)}
                onMoveToTrash={() =>
                  setTrashTarget({ kind: 'file', id: file.id, name: file.originalName })
                }
                onRestore={() => unarchiveMut.mutate(file.id)}
                onPurgeNow={() => setDeleteTarget(file)}
                isEditingName={editingFileId === file.id}
                canStartEditingName={editingFileId === null}
                onStartEditingName={() => setEditingFileId(file.id)}
                onStopEditingName={() => setEditingFileId(null)}
              />
            ))}
          </ul>
        </>
      )}

      <MoveToTrashModal
        target={trashTarget}
        onClose={() => setTrashTarget(null)}
        onConfirm={() => {
          if (trashTarget?.kind === 'file') archiveMut.mutate(trashTarget.id)
        }}
        isPending={archiveMut.isPending}
      />

      <DeleteFileModal
        file={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        projectId={projectId}
      />
      <FileViewerModal file={previewTarget} onClose={() => setPreviewTarget(null)} />
      <CreateShareLinkDialog
        open={shareDialogOpen}
        onClose={() => {
          setShareDialogOpen(false)
          // ダイアログを閉じたら選択もクリア（次の操作のために身軽にしておく）
          setBulkSelectedIds(new Set())
        }}
        projectId={projectId}
        initialFileIds={shareInitialFileIds}
      />
    </div>
  )
}

/**
 * 選択モード時に一覧の最上部に表示するツールバー。
 *
 * 設計意図 (issue #32):
 *   - 共有 URL 作成のフローは「ファイルにチェックを入れて → アクションを選ぶ」のたった 1 経路に統一
 *   - タブやその他のボタンと並べると煩雑なので、選択中は完全に置き換える（モード切替）
 *   - 中央のアクションは現状 1 つだけ（共有）。将来「まとめて削除」「まとめて DL」を追加できる場所として確保
 */
function SelectionActionBar({
  selectedCount,
  onClear,
  onShare,
}: {
  selectedCount: number
  onClear: () => void
  onShare: () => void
}) {
  return (
    <div className="flex items-center gap-3 px-3 py-2 bg-muted/60 border border-border rounded-lg">
      <button
        type="button"
        onClick={onClear}
        aria-label="選択を解除"
        title="選択を解除"
        className="flex-shrink-0 p-1.5 rounded-full hover:bg-foreground/10 transition-colors text-foreground"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      <span className="text-sm font-medium">
        <span className="editorial-num">{selectedCount}</span>
        <span className="ml-1 text-muted-foreground">件選択中</span>
      </span>
      <div className="flex-1" />
      <button
        type="button"
        onClick={onShare}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
        共有URLを作成
      </button>
    </div>
  )
}

function BulkSelectionHeader({
  total,
  selected,
  onSelectAll,
  onClear,
}: {
  total: number
  selected: number
  onSelectAll: () => void
  onClear: () => void
}) {
  const allChecked = total > 0 && selected === total
  return (
    <div className="flex items-center justify-between text-xs text-muted-foreground pb-1 px-1">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={allChecked}
          onChange={() => (allChecked ? onClear() : onSelectAll())}
          aria-label={allChecked ? 'すべて解除' : 'すべて選択'}
        />
        <span>{allChecked ? 'すべて解除' : 'すべて選択'}</span>
      </label>
      <span>計 {total.toLocaleString('ja-JP')} 件</span>
    </div>
  )
}

function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean
  onClick: () => void
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
        active
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {label}
    </button>
  )
}

function FileRow({
  file,
  isSelected,
  bulkChecked,
  bulkSelectable,
  isInSelectionMode,
  onToggleBulkChecked,
  busy,
  onPlayVideo,
  onPreview,
  onMoveToTrash,
  onRestore,
  onPurgeNow,
  isEditingName,
  canStartEditingName,
  onStartEditingName,
  onStopEditingName,
}: {
  file: FileResource
  isSelected: boolean
  /** 一括選択チェックボックスの状態 */
  bulkChecked: boolean
  /** チェックボックスを表示するか（ゴミ箱タブ・アップロード未完了では非表示） */
  bulkSelectable: boolean
  /**
   * 一覧全体が選択モードに入っているか（1 件以上チェック済み）。
   * true の時はカード地クリックでもチェック切替できるようにする（Gmail / Drive 風）。
   */
  isInSelectionMode: boolean
  onToggleBulkChecked: () => void
  busy: boolean
  /** 動画再生（VideoPlayer 展開）。動画ファイル以外では呼ばれない。 */
  onPlayVideo: () => void
  /** 画像 / PDF のフルプレビュー モーダル。 */
  onPreview: () => void
  /** ゴミ箱へ移す（確認モーダル経由） */
  onMoveToTrash: () => void
  /** ゴミ箱から元に戻す */
  onRestore: () => void
  /** 今すぐ完全に消す（ゴミ箱タブのみ） */
  onPurgeNow: () => void
  /** ファイル名編集モードに入っているか（親が一意に管理） */
  isEditingName: boolean
  /** 他に編集中の行がないか（true なら鉛筆ボタンを有効化） */
  canStartEditingName: boolean
  onStartEditingName: () => void
  onStopEditingName: () => void
}) {
  const isArchived = !!file.archivedAt
  const isUploaded = !!file.uploadedAt
  const kind = classifyFile(file.mimeType)
  const canPlayVideo = kind === 'video' && file.streamStatus === 'READY'
  const canPreview = isUploaded && isPreviewable(kind)
  const sizeBytes = Number(file.sizeBytes)

  // サムネクリック時の挙動: 動画は再生、画像/PDF はプレビュー、その他は何もしない
  const onThumbnailClick = canPlayVideo ? onPlayVideo : canPreview ? onPreview : null

  // 選択モード中はカード地クリックでもチェック切替できる（Gmail / Drive 風）。
  // ただしカード内のボタン・リンク・入力要素・チェックボックスのラベル上の
  // クリックは各々の本来の挙動を優先するため除外する。
  // bulkSelectable が false（ゴミ箱内・アップロード未完了）の行は対象外。
  const rowClickToggles = isInSelectionMode && bulkSelectable
  const onRowClick = rowClickToggles
    ? (e: React.MouseEvent<HTMLLIElement>) => {
        const target = e.target as HTMLElement
        if (target.closest('button, a, input, label')) return
        onToggleBulkChecked()
      }
    : undefined

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: キーボード操作は内部の checkbox (Tab → Space) で済むため、li 自体の onClick はマウス向けの追加ヒット領域として機能する
    <li
      onClick={onRowClick}
      className={`group border rounded-lg bg-card flex flex-wrap md:flex-nowrap items-center gap-3 md:gap-4 p-3 transition-colors ${
        rowClickToggles ? 'cursor-pointer' : ''
      } ${
        isSelected
          ? 'border-primary ring-1 ring-primary/20'
          : bulkChecked
            ? 'border-primary/40 bg-primary/[0.03]'
            : 'border-border hover:border-foreground/30'
      }`}
    >
      {bulkSelectable && (
        <label
          aria-label={`${file.originalName} を選択`}
          className="-my-3 -ml-3 self-stretch flex items-center justify-center pl-3 pr-1 cursor-pointer hover:bg-muted/60 transition-colors rounded-l-lg"
        >
          <input
            type="checkbox"
            checked={bulkChecked}
            onChange={onToggleBulkChecked}
            className="cursor-pointer"
          />
        </label>
      )}
      <Thumbnail
        file={file}
        kind={kind}
        canPlayVideo={canPlayVideo}
        canPreview={canPreview}
        onClick={onThumbnailClick}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <FileNameEditable
            file={file}
            canPreview={canPreview}
            onPreview={onPreview}
            isEditing={isEditingName}
            canStartEditing={canStartEditingName}
            onStartEditing={onStartEditingName}
            onStopEditing={onStopEditingName}
          />
          <StatusBadge
            status={file.streamStatus}
            uploaded={isUploaded}
            kind={kind}
            uploadedAt={file.uploadedAt}
          />
          {isArchived && <TrashBadge archivedAt={file.archivedAt as string} />}
        </div>
        <div className="text-xs text-muted-foreground mt-1 font-mono editorial-num">
          {formatBytes(sizeBytes)}
          {file.durationSec != null && <> · {formatDuration(file.durationSec)}</>}
          {file.uploadedAt && <> · {formatDateTimeJa(file.uploadedAt)}</>}
        </div>
      </div>
      <FileRowActions
        file={file}
        kind={kind}
        canPlayVideo={canPlayVideo}
        canPreview={canPreview}
        isUploaded={isUploaded}
        isArchived={isArchived}
        busy={busy}
        onPlayVideo={onPlayVideo}
        onPreview={onPreview}
        onMoveToTrash={onMoveToTrash}
        onRestore={onRestore}
        onPurgeNow={onPurgeNow}
      />
    </li>
  )
}

function FileRowActions({
  file,
  kind,
  canPlayVideo,
  canPreview,
  isUploaded,
  isArchived,
  busy,
  onPlayVideo,
  onPreview,
  onMoveToTrash,
  onRestore,
  onPurgeNow,
}: {
  file: FileResource
  kind: FileKind
  canPlayVideo: boolean
  canPreview: boolean
  isUploaded: boolean
  isArchived: boolean
  busy: boolean
  onPlayVideo: () => void
  onPreview: () => void
  onMoveToTrash: () => void
  onRestore: () => void
  onPurgeNow: () => void
}) {
  const onDownload = async () => {
    try {
      const { url } = await getDownloadUrl(file.id)
      window.location.href = url
    } catch (err) {
      alert(toUserMessage(err, SUBMIT_ERROR_FALLBACK))
    }
  }

  return (
    // モバイル: 操作ボタンが多い (再生/プレビュー/DL/削除 等) と
    // 横幅 360px では収まらないので、行の幅いっぱいを使って 2 行に折り返す。
    // md+ では従来通り右端 1 行に並べる。
    <div className="flex items-center gap-2 flex-wrap justify-end w-full md:w-auto md:flex-nowrap md:flex-shrink-0">
      {kind === 'video' && (
        <button
          type="button"
          onClick={onPlayVideo}
          disabled={!canPlayVideo}
          className="px-3 py-1.5 border border-border rounded-md text-sm font-medium hover:bg-accent transition-colors disabled:opacity-50"
        >
          再生
        </button>
      )}
      {canPreview && (
        <button
          type="button"
          onClick={onPreview}
          className="px-3 py-1.5 border border-border rounded-md text-sm font-medium hover:bg-accent transition-colors"
        >
          プレビュー
        </button>
      )}
      <button
        type="button"
        onClick={onDownload}
        disabled={!isUploaded}
        className="px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
      >
        DL
      </button>
      {isArchived ? (
        <>
          <button
            type="button"
            onClick={onRestore}
            disabled={busy}
            className="px-3 py-1.5 border border-border rounded-md text-sm font-medium hover:bg-accent transition-colors disabled:opacity-50"
            title="元の一覧に戻す"
          >
            {TRASH_ACTION.restore}
          </button>
          <button
            type="button"
            onClick={onPurgeNow}
            aria-label={`${file.originalName} を今すぐ完全に消す`}
            className="px-2 py-1.5 border border-destructive/30 text-destructive rounded-md text-sm font-medium hover:bg-destructive/5 transition-colors"
            title="今すぐ完全に消す（元に戻せません）"
          >
            {TRASH_ACTION.purgeNow}
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={onMoveToTrash}
          disabled={busy}
          className="px-3 py-1.5 border border-border rounded-md text-sm font-medium hover:bg-accent transition-colors disabled:opacity-50"
          title="ゴミ箱に移す（30日後に自動で消えます）"
        >
          {TRASH_ACTION.delete}
        </button>
      )}
    </div>
  )
}

/**
 * ゴミ箱内ファイルの状態バッジ。
 * `archivedAt + 30 日` までの残日数を計算して表示する。
 */
function TrashBadge({ archivedAt }: { archivedAt: string }) {
  const remainingDays = getTrashRemainingDays(archivedAt)
  const purgeAt = new Date(
    new Date(archivedAt).getTime() + TRASH_RETENTION_DAYS * 24 * 60 * 60 * 1000,
  )
  return (
    <span
      className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-900"
      title={`${purgeAt.toLocaleString('ja-JP')} に自動で完全に消えます`}
    >
      {TRASH_ACTION.trashedBadge} · 残 {remainingDays} 日
    </span>
  )
}

/**
 * ファイル種別に応じたサムネイル表示。
 *   - 動画 (READY): Cloudflare Stream の署名済みサムネ URL（lazy 取得・画面に入った時点で発行）
 *   - 画像: 原本（R2 署名 URL）を `<img>` で lazy-load 表示
 *   - PDF / 音声 / その他: 種別アイコン
 */
function Thumbnail({
  file,
  kind,
  canPlayVideo,
  canPreview,
  onClick,
}: {
  file: FileResource
  kind: FileKind
  canPlayVideo: boolean
  canPreview: boolean
  onClick: (() => void) | null
}) {
  // ビューポートに入ったかどうか。入ってから初めてサムネ URL を取得する。
  // ラッパー要素は button or div の 2 通りなので Element 型で受ける。
  const wrapperRef = useRef<HTMLElement>(null)
  const inView = useInView(wrapperRef)

  // 画像のみ署名URLを引いてサムネ表示
  const isImage = kind === 'image' && !!file.uploadedAt
  const { url: imageUrl } = usePreviewUrl(file.id, { enabled: isImage && inView })

  // 動画は READY のときだけ署名済みサムネ URL を個別取得
  const isReadyVideo = kind === 'video' && file.streamStatus === 'READY' && !!file.streamUid
  const { url: videoThumbnailUrl } = useStreamThumbnailUrl(file.id, {
    enabled: isReadyVideo && inView,
  })

  const clickable = !!onClick
  const wrapperBaseClass =
    'w-24 h-16 rounded-md overflow-hidden flex-shrink-0 relative ring-1 transition-all'

  const overlay = canPlayVideo ? '▶' : canPreview ? '🔍' : null

  const inner = (
    <>
      {/* 動画: Stream の署名済みサムネ */}
      {videoThumbnailUrl && (
        <img
          src={videoThumbnailUrl}
          alt=""
          className="w-full h-full object-cover bg-muted"
          loading="lazy"
        />
      )}
      {/* 画像: 原本そのもの（R2 署名 URL） */}
      {!videoThumbnailUrl && isImage && imageUrl && (
        <img src={imageUrl} alt="" className="w-full h-full object-cover bg-muted" loading="lazy" />
      )}
      {/* フォールバック: アイコン */}
      {!videoThumbnailUrl && (!isImage || !imageUrl) && (
        <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground text-2xl">
          {iconForKind(kind)}
        </div>
      )}
      {/* hover オーバーレイ */}
      {clickable && overlay && (
        <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors">
          <span className="opacity-0 group-hover:opacity-100 text-white text-lg">{overlay}</span>
        </span>
      )}
    </>
  )

  if (clickable) {
    return (
      <button
        ref={wrapperRef as React.RefObject<HTMLButtonElement>}
        type="button"
        onClick={onClick}
        className={`${wrapperBaseClass} ring-border hover:ring-foreground/40 cursor-pointer`}
        aria-label={
          canPlayVideo
            ? `${file.originalName} を再生`
            : canPreview
              ? `${file.originalName} をプレビュー`
              : file.originalName
        }
      >
        {inner}
      </button>
    )
  }
  return (
    <div
      ref={wrapperRef as React.RefObject<HTMLDivElement>}
      className={`${wrapperBaseClass} ring-border/60`}
    >
      {inner}
    </div>
  )
}

/**
 * ファイル名の表示 + インライン編集 UI。
 *
 * - 通常時: ファイル名（プレビュー可なら button、それ以外 span）の隣に
 *   hover で薄く出る鉛筆アイコン
 * - 編集時: input + 「保存 / キャンセル」
 *   - Enter で保存、Esc でキャンセル、blur では確定しない（誤確定防止）
 *   - フォーカス時は拡張子の前まで自動選択（拡張子の誤変更を防ぐ）
 *   - 変更なし / 空文字はサーバを叩かずキャンセル扱い
 *
 * 編集状態は親 (FileList) が単一の `editingFileId` で管理するため、
 * 同時に複数行が編集モードに入ることはない。
 */
function FileNameEditable({
  file,
  canPreview,
  onPreview,
  isEditing,
  canStartEditing,
  onStartEditing,
  onStopEditing,
}: {
  file: FileResource
  canPreview: boolean
  onPreview: () => void
  isEditing: boolean
  canStartEditing: boolean
  onStartEditing: () => void
  onStopEditing: () => void
}) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [draft, setDraft] = useState(file.originalName)
  const inputRef = useRef<HTMLInputElement>(null)

  const renameMut = useMutation({
    mutationFn: (newName: string) => renameFile(file.id, newName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] })
      toast.success('ファイル名を変更しました')
      onStopEditing()
    },
    onError: (err) => {
      toast.error(toUserMessage(err, SUBMIT_ERROR_FALLBACK))
    },
  })

  // 親が編集モードを切り替えたタイミングで draft を最新値に初期化し、input をフォーカス。
  // biome-ignore lint/correctness/useExhaustiveDependencies: isEditing の遷移をトリガーとして使うため originalName を deps に含めない（編集中に外部更新が来ても draft を上書きしない）
  useEffect(() => {
    if (!isEditing) return
    setDraft(file.originalName)
    const el = inputRef.current
    if (!el) return
    el.focus()
    // 拡張子の前まで選択して、誤って拡張子を消すのを防ぐ
    const lastDot = el.value.lastIndexOf('.')
    if (lastDot > 0) el.setSelectionRange(0, lastDot)
    else el.select()
  }, [isEditing])

  const cancel = () => {
    setDraft(file.originalName)
    onStopEditing()
  }
  const submit = () => {
    const trimmed = draft.trim()
    if (trimmed.length === 0 || trimmed === file.originalName) {
      cancel()
      return
    }
    renameMut.mutate(trimmed)
  }

  if (isEditing) {
    return (
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit()
            else if (e.key === 'Escape') cancel()
          }}
          disabled={renameMut.isPending}
          className="flex-1 min-w-0 px-2 py-1 text-sm font-medium border border-primary/40 rounded focus:outline-none focus:ring-2 focus:ring-primary/30"
          aria-label="ファイル名を編集"
        />
        <button
          type="button"
          onClick={submit}
          disabled={renameMut.isPending}
          className="text-xs px-2 py-1 rounded bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          保存
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={renameMut.isPending}
          className="text-xs px-2 py-1 rounded border border-border hover:bg-accent disabled:opacity-50"
        >
          キャンセル
        </button>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-1.5 min-w-0">
      {canPreview ? (
        <button
          type="button"
          onClick={onPreview}
          className="font-medium truncate text-left hover:underline underline-offset-4 decoration-foreground/30"
        >
          {file.originalName}
        </button>
      ) : (
        <span className="font-medium truncate">{file.originalName}</span>
      )}
      <button
        type="button"
        onClick={onStartEditing}
        disabled={!canStartEditing}
        aria-label="ファイル名を編集"
        title={canStartEditing ? 'ファイル名を編集' : '他のファイルを編集中です'}
        className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-muted-foreground hover:text-foreground p-1 rounded hover:bg-accent flex-shrink-0 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground/40 disabled:text-muted-foreground/40"
      >
        {/* biome-ignore lint/a11y/noSvgWithoutTitle: aria-label を親 button に付与しているため SVG 自体は装飾 */}
        <svg
          aria-hidden
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
        </svg>
      </button>
    </div>
  )
}

function StatusBadge({
  status,
  uploaded,
  kind,
  uploadedAt,
}: {
  status: FileResource['streamStatus']
  uploaded: boolean
  kind: FileKind
  uploadedAt: string | null
}) {
  if (!uploaded) {
    return (
      <span className="text-xs px-2 py-0.5 rounded bg-yellow-100 text-yellow-800">
        アップロード中
      </span>
    )
  }
  if (kind !== 'video') return null
  if (status === 'READY') {
    return <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-800">再生可能</span>
  }
  if (status === 'ERROR') {
    return <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-800">エラー</span>
  }
  // 変換中: 進捗値は Cloudflare 側から取れないので、スピナーと経過時間で「動いてる感」を出す。
  // 経過時間は親の 10s polling で再計算される（StatusBadge 自身は interval を持たない）。
  // 1 分未満は数字を表示しない: 親 polling 周期 (10s) と相性が悪く「4秒経過」のまま固まって見える上に、
  // 「すぐ終わる」という誤解も招くため。1 分以上はスケール感が合うので分単位で出す。
  const elapsedLabel = uploadedAt ? formatElapsedSince(uploadedAt) : null
  return (
    <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-800 inline-flex items-center gap-1.5">
      <ConvertingSpinner />
      <span>変換中</span>
      {elapsedLabel && <span className="text-blue-700/70">（{elapsedLabel}）</span>}
    </span>
  )
}

function ConvertingSpinner() {
  return (
    // biome-ignore lint/a11y/noSvgWithoutTitle: 隣の「変換中」テキストが既に意味を伝えるため、SVG は装飾扱い（aria-hidden）にする
    <svg
      aria-hidden
      className="animate-spin h-3 w-3"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
    >
      <circle cx="12" cy="12" r="10" strokeWidth="3" opacity="0.25" />
      <path strokeWidth="3" strokeLinecap="round" d="M4 12a8 8 0 018-8" />
    </svg>
  )
}

/**
 * 日時を `yyyy/MM/dd HH:mm` 形式で出す。
 * 秒・タイムゾーンは出さない（カードのメタ行の桁数を抑える）。
 */
function formatDateTimeJa(iso: string): string {
  const d = new Date(iso)
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mi = String(d.getMinutes()).padStart(2, '0')
  return `${yyyy}/${mm}/${dd} ${hh}:${mi}`
}

function formatElapsedSince(iso: string): string | null {
  const start = new Date(iso).getTime()
  const sec = Math.max(0, Math.floor((Date.now() - start) / 1000))
  // 1 分未満はラベル自体を出さない（呼出側で null を弾く）
  if (sec < 60) return null
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}分経過`
  const hour = Math.floor(min / 60)
  const remMin = min % 60
  return remMin === 0 ? `${hour}時間経過` : `${hour}時間${remMin}分経過`
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m}:${String(s).padStart(2, '0')}`
}
